import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete, TextField, CircularProgress, Box, Typography, Chip, Button,
} from '@mui/material';
import { AddCircleOutline as CreateIcon } from '@mui/icons-material';
import debounce from 'lodash/debounce';
import wineService, { parseVintageInput } from '../../services/wineService';
import type { Wine, WineSearchResult } from '../../services/wineService';
import venueWineRequestService from '../../services/venueWineRequestService';
import { useAuth } from '../../context/AuthContext';
import RequestWineModal from './RequestWineModal';
import { wineColorDotFor, normalizeWineColor, NEUTRAL_WINE_DOT } from '../../utils/wineColor';

export type { WineSearchResult };

// Sentinel option appended at the bottom of the dropdown
const CREATE_OPTION_ID = '__create_wine__';
type CreateOption = { _id: typeof CREATE_OPTION_ID; name: string; __isCreate__: true };

/**
 * One dropdown row per wine NAME. `wines` are that name's existing vintages,
 * rendered as chips. Grouping key includes producer/region so two distinct
 * wine names sharing a display name don't collapse into one row.
 */
export interface WineGroupOption {
  __isGroup__: true;
  key: string;
  label: string; // wine name without the vintage suffix
  producerName?: string;
  regionName?: string;
  color?: string;
  wines: WineSearchResult[];
}

type DropdownOption = WineSearchResult | WineGroupOption | CreateOption;

const isCreateOption = (o: DropdownOption): o is CreateOption =>
  (o as CreateOption).__isCreate__ === true;

const isGroupOption = (o: DropdownOption): o is WineGroupOption =>
  (o as WineGroupOption).__isGroup__ === true;

/** wine.name is stored as `${wineName.displayName} ${displayVintage}` — strip the suffix. */
const baseNameOf = (w: WineSearchResult): string => {
  const name = w.name ?? '';
  if (w.displayVintage && name.endsWith(` ${w.displayVintage}`)) {
    return name.slice(0, -(w.displayVintage.length + 1));
  }
  return name;
};

/** Chip order: years ascending, then NV, then MV. */
const vintageSortKey = (w: WineSearchResult): number => {
  if (w.vintageType === 'non_vintage') return 100000;
  if (w.vintageType === 'multi_vintage') return 100001;
  const year = Number(w.displayVintage);
  return Number.isFinite(year) ? year : 99999;
};

/**
 * FIXME(review): the grouping key is name-based (label + producer + region)
 * because the wine search projection carries no wineName id — verified in
 * wine.service.ts, which returns only {_id, name, displayVintage,
 * vintageType, wineDisplay{producerName, regionName, color}}. Two distinct
 * wine names that agree on all three strings therefore merge into one row,
 * and one name whose producer/region strings differ across records splits
 * into two. Re-key on wineNameId once the search response carries it (one
 * field added to the shared projection); the "+ add year" resolution below
 * would then need no lookup at all.
 */
export const groupWinesByName = (wines: WineSearchResult[]): WineGroupOption[] => {
  const groups = new Map<string, WineGroupOption>();
  for (const wine of wines) {
    const label = baseNameOf(wine);
    const key = [label, wine.wineDisplay?.producerName ?? '', wine.wineDisplay?.regionName ?? ''].join('::');
    let group = groups.get(key);
    if (!group) {
      group = {
        __isGroup__: true,
        key,
        label,
        producerName: wine.wineDisplay?.producerName,
        regionName: wine.wineDisplay?.regionName,
        color: wine.wineDisplay?.color,
        wines: [],
      };
      groups.set(key, group);
    }
    group.wines.push(wine);
  }
  const result = Array.from(groups.values());
  result.forEach(g => g.wines.sort((a: WineSearchResult, b: WineSearchResult) => vintageSortKey(a) - vintageSortKey(b)));
  return result;
};

/**
 * The backend rejects duplicate (wineName, vintageType, vintage) identities.
 * Wine create currently maps this to 409; the winename endpoint still leaks
 * a 500 carrying the raw E11000 — treat both as "already exists".
 */
const isDuplicateWineError = (err: any): boolean => {
  const status = err?.response?.status;
  if (status === 409) return true;
  if (status === 500) {
    const body = err?.response?.data;
    const text = typeof body === 'string' ? body : JSON.stringify(body ?? {});
    return text.includes('E11000');
  }
  return false;
};

const DUPLICATE_COPY = 'This wine already exists — pick it from the list above.';

/**
 * Parent-driven search seed. `nonce` makes repeat seeds of the same query
 * re-trigger — used when the create modal hands back an existing wine name.
 */
export interface WineSearchSeed {
  query: string;
  nonce: number;
}

interface Props {
  value: WineSearchResult | null;
  onChange: (wine: WineSearchResult | null) => void;
  onCreateNew?: (query: string) => void;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  label?: string;
  /**
   * When provided, search hits the venue-scoped lookup endpoint
   * (`/admin/venue/:venueId/wine-lookup/search`) which is open to VENUE_USER.
   * Omit only from admin-context callers operating outside any venue.
   */
  venueId?: string;
  /** Run this search immediately and open the dropdown. See WineSearchSeed. */
  seed?: WineSearchSeed;
}

const WineSearchBox: React.FC<Props> = ({
  value,
  onChange,
  onCreateNew,
  error,
  helperText,
  disabled,
  label = 'Wine',
  venueId,
  seed,
}) => {
  const { isVenueUser } = useAuth();
  // Two creation modes share the same sentinel slot:
  //   - VENUE_USER inside a venue (venueId present) → opens RequestWineModal
  //     internally. The result is a provisional wine (needsReview, source =
  //     'venue_request') wired straight back through onChange.
  //   - ADMIN/SYSTEM_ADMIN with a parent-provided onCreateNew → fires the
  //     callback, parent owns CreateWineModal as before.
  // Both modes require that the parent expressed creation intent (passed
  // onCreateNew); WineSearchBox itself never invents one.
  const venueRequestMode = !!onCreateNew && isVenueUser() && !!venueId;
  const adminCreateMode = !!onCreateNew && !isVenueUser();
  const canCreate = venueRequestMode || adminCreateMode;
  // Which door "+ add year" writes through. Deliberately NOT venueRequestMode:
  // that also requires onCreateNew (the modal's creation intent), and a venue
  // user without it would otherwise fall through to the admin-only endpoints.
  const venueAddYearMode = isVenueUser() && !!venueId;
  // A venue user outside a venue context has no door at all.
  const canAddYear = !isVenueUser() || !!venueId;

  const [options, setOptions] = useState<WineSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  // Local state for the venue-user request flow. Admin's CreateWineModal is
  // still owned by the parent, so we don't manage it here.
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [requestModalQuery, setRequestModalQuery] = useState('');
  // Keyboard navigation inside a group row. MUI keeps real DOM focus on the
  // input and drives the list with aria-activedescendant; moving focus into a
  // chip would read as a blur and close the popup. So chip focus is virtual:
  // MUI still owns row highlighting, and this cursor owns the chips within
  // the highlighted row.
  const [highlighted, setHighlighted] = useState<DropdownOption | null>(null);
  const [chipCursor, setChipCursor] = useState<{ groupKey: string; index: number } | null>(null);
  // "+ add year" inline flow: one group at a time.
  const [addYearGroup, setAddYearGroup] = useState<WineGroupOption | null>(null);
  const [yearInput, setYearInput] = useState('');
  const [addYearBusy, setAddYearBusy] = useState(false);
  const [addYearError, setAddYearError] = useState('');

  // Hold the current venueId in a ref so the long-lived debounced function
  // always reads the latest value without being rebuilt on every render.
  const venueIdRef = useRef(venueId);
  useEffect(() => { venueIdRef.current = venueId; }, [venueId]);

  // Stable (no deps — venueId is read through its ref), so the debounced
  // wrapper below can capture it once on mount.
  const runSearch = useCallback(async (q: string) => {
    if (q.trim().length < 2) {
      setOptions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setSearchFailed(false);
    try {
      const results = await wineService.search(q, 10, venueIdRef.current);
      setOptions(results);
    } catch {
      setOptions([]);
      setSearchFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const debouncedSearch = useRef(debounce(runSearch, 300)).current;

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  // Last user-typed query. MUI resets the controlled input (to the selected
  // option's label — '' for sentinels) before onChange fires, so the create
  // flow can't rely on inputValue for the prefill query.
  const lastTypedQueryRef = useRef('');

  const handleInputChange = useCallback((_: React.SyntheticEvent, newInput: string, reason?: string) => {
    if (reason === 'input') lastTypedQueryRef.current = newInput;
    setInputValue(newInput);
    setSearchFailed(false);
    if (newInput.trim().length < 2) {
      setOptions([]);
      setLoading(false);
      debouncedSearch.cancel();
      return;
    }
    setLoading(true);
    debouncedSearch(newInput);
  }, [debouncedSearch]);

  const createOption: CreateOption = {
    _id: CREATE_OPTION_ID,
    name: venueRequestMode ? '+ Request new wine' : '+ Create new wine',
    __isCreate__: true,
  };

  const groupedOptions = useMemo(() => groupWinesByName(options), [options]);

  // Append the sentinel only when creation is allowed for this user.
  const dropdownOptions: DropdownOption[] = canCreate
    ? [...groupedOptions, createOption]
    : [...groupedOptions];

  const noOptionsText = (() => {
    if (inputValue.trim().length < 2) return 'Type at least 2 characters to search';
    if (loading) return 'Searching…';
    if (searchFailed) return 'Search failed — check your connection';
    return 'No wines found';
  })();

  // Wine (full populated record) → WineSearchResult (lightweight projection).
  // Mirror of the shape returned by GET /admin/wine/search.
  const toSearchResult = (wine: Wine): WineSearchResult => ({
    _id: wine._id,
    name: wine.name,
    displayVintage: wine.displayVintage,
    vintageType: wine.vintageType,
    wineDisplay: {
      producerName: wine.wineName?.producer?.name,
      regionName: wine.wineName?.region?.name,
      color: wine.wineName?.color,
    },
  });

  const resetAddYear = () => {
    setAddYearGroup(null);
    setYearInput('');
    setAddYearError('');
  };

  // Parent seeded a query (e.g. the create modal handed back the existing
  // wine name it refused to duplicate) — search it now and open the list so
  // its vintage chips and "+ add year" are one tap away.
  // FIXME(review): dead loop when the seeded wine name has no *findable*
  // wines — it exists but every wine on it is inactive, or it has none at
  // all. The wine search only returns active wines, so the user lands on
  // "No wines found" with no row and therefore no "+ add year" chip, and the
  // create form they came from will keep refusing the same duplicate. Needs
  // an empty-seed fallback (offer add-year against the seeded wineNameId
  // directly, which means the seed must carry the id, not just the name).
  useEffect(() => {
    const query = seed?.query?.trim();
    if (!query) return;
    debouncedSearch.cancel();
    setInputValue(query);
    lastTypedQueryRef.current = query;
    setAddYearGroup(null);
    setAddYearError('');
    setOpen(true);
    runSearch(query);
    // Re-runs on every seed, including a repeat of the same query.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed?.nonce]);

  const selectWine = useCallback((wine: WineSearchResult) => {
    onChange(wine);
    setInputValue(wine.name ?? '');
    setOptions([]);
    setOpen(false);
    resetAddYear();
  }, [onChange]);

  const startAddYear = (group: WineGroupOption) => {
    setAddYearGroup(group);
    setYearInput('');
    setAddYearError('');
    setOpen(false);
  };

  /**
   * Resolve the group's wineNameId. The wine search projection carries no
   * wineName id in any field, so it has to be looked up — and the lookup has
   * to stay inside the caller's own door:
   *   venue-scoped  → GET /wine-lookup/wine-names/search, open to VENUE_OPS
   *                   (matched on the group label, which IS the displayName)
   *   admin, no venue → GET /admin/wine/:id on one group member
   * A VENUE_USER must never reach the admin endpoint — that 403s.
   */
  const resolveWineName = async (
    group: WineGroupOption,
  ): Promise<{ id: string; displayName: string } | null> => {
    if (venueId) {
      const matches = await venueWineRequestService.searchWineNames(venueId, group.label);
      const target = group.label.trim().toLowerCase();
      const exact = matches.find(m => (m.displayName ?? '').trim().toLowerCase() === target);
      if (exact) return { id: exact._id, displayName: exact.displayName };
      // Venue users have no second door; admins fall through to theirs.
      if (isVenueUser()) return null;
    }
    if (isVenueUser()) return null;
    const sample = await wineService.getById(group.wines[0]._id);
    return sample.wineName?._id
      ? { id: sample.wineName._id, displayName: sample.wineName.displayName }
      : null;
  };

  /**
   * Find-or-create the wine for (this group's wineNameId, entered vintage),
   * then select it. Admin door: check-duplicate (the "find") then POST
   * /admin/wine; venue door: the request endpoint with an existing
   * wineNameId (no producer/region form either way).
   */
  const handleAddYearConfirm = async () => {
    if (!addYearGroup) return;
    const parsed = parseVintageInput(yearInput);
    if (!parsed.valid || parsed.vintageType === 'multi_vintage'
      || (parsed.vintageType === 'single_vintage' && !/^\d{4}$/.test(yearInput.trim()))) {
      setAddYearError(parsed.error ?? 'Enter a 4-digit year (e.g. 2018) or use NV');
      return;
    }
    setAddYearBusy(true);
    setAddYearError('');
    try {
      const wineName = await resolveWineName(addYearGroup);
      if (!wineName) throw new Error('Could not resolve the wine name for this group');
      const wineNameId = wineName.id;

      if (venueAddYearMode && venueId) {
        try {
          const wine = await venueWineRequestService.requestNew(venueId, {
            wineNameId,
            vintageType: parsed.vintageType,
            vintage: parsed.vintage,
          });
          selectWine(toSearchResult(wine));
        } catch (err) {
          if (!isDuplicateWineError(err)) throw err;
          // Vintage already exists — find it by its deterministic name and select it.
          const expectedName = `${wineName.displayName} ${parsed.displayVintage}`.trim();
          const results = await wineService.search(expectedName, 10, venueId);
          const existing = results.find(r => r.name === expectedName);
          if (existing) selectWine(existing);
          else setAddYearError(DUPLICATE_COPY);
        }
        return;
      }

      // Admin door: find first…
      const dup = await wineService.checkDuplicate({
        wineNameId,
        vintageType: parsed.vintageType,
        vintage: parsed.vintage,
      });
      if (dup.isDuplicate && dup.existing) {
        selectWine(toSearchResult(dup.existing));
        return;
      }
      // …then create.
      try {
        const wine = await wineService.create({
          wineNameId,
          vintageType: parsed.vintageType,
          vintage: parsed.vintage,
          active: true,
        });
        selectWine(toSearchResult(wine));
      } catch (err) {
        if (!isDuplicateWineError(err)) throw err;
        // Raced with someone else creating it — re-find and select.
        const retry = await wineService.checkDuplicate({
          wineNameId,
          vintageType: parsed.vintageType,
          vintage: parsed.vintage,
        });
        if (retry.isDuplicate && retry.existing) selectWine(toSearchResult(retry.existing));
        else setAddYearError(DUPLICATE_COPY);
      }
    } catch (err: any) {
      setAddYearError(isDuplicateWineError(err) ? DUPLICATE_COPY : 'Failed to add this vintage. Please try again.');
    } finally {
      setAddYearBusy(false);
    }
  };

  const chipCountOf = (group: WineGroupOption) => group.wines.length + (canAddYear ? 1 : 0);

  /** Activate the chip at `index`: a vintage selects, the last one adds a year. */
  const activateChip = (group: WineGroupOption, index: number) => {
    setChipCursor(null);
    if (index < group.wines.length) selectWine(group.wines[index]);
    else startAddYear(group);
  };

  /**
   * Keyboard contract for group rows. Runs before MUI's own handler;
   * `defaultMuiPrevented` is MUI's documented opt-out. Tab is never touched,
   * so focus can always leave. Escape with no chip cursor falls through to
   * MUI, which closes the popup — focus is still on the input, where it has
   * been the whole time.
   */
  const handleListKeyDown = (event: React.KeyboardEvent) => {
    if (!open) return;
    // Enter while the list is open must never reach an enclosing <form> —
    // WineListItemForm wraps this picker in one, and MUI does not suppress
    // the browser default when nothing is highlighted, so a stray Enter
    // would submit a half-filled form. Suppressing the native default here
    // leaves MUI's own Enter handling (option selection) untouched; we only
    // take that over below via defaultMuiPrevented. Closed list falls
    // through untouched, so normal submit-on-Enter still works.
    if (event.key === 'Enter') event.preventDefault();

    const group = highlighted && isGroupOption(highlighted) ? highlighted : null;
    if (!group) return;
    const cursor = chipCursor?.groupKey === group.key ? chipCursor : null;
    const stop = () => {
      (event as any).defaultMuiPrevented = true;
      event.preventDefault();
    };

    switch (event.key) {
      case 'Enter':
        stop();
        if (cursor) { activateChip(group, cursor.index); return; }
        // Exactly one vintage — nothing to disambiguate, take it.
        if (group.wines.length === 1) { selectWine(group.wines[0]); return; }
        // Otherwise step into the row's chips.
        setChipCursor({ groupKey: group.key, index: 0 });
        return;

      case 'ArrowRight': {
        if (!cursor) {
          // Only step in from the end of the query — mid-text, Right still
          // moves the caret.
          const input = event.target as HTMLInputElement | null;
          const atEnd = !input?.value || (input.selectionStart === input.value.length
            && input.selectionStart === input.selectionEnd);
          if (!atEnd) return;
          stop();
          setChipCursor({ groupKey: group.key, index: 0 });
          return;
        }
        stop();
        setChipCursor({ groupKey: group.key, index: Math.min(cursor.index + 1, chipCountOf(group) - 1) });
        return;
      }

      case 'ArrowLeft':
        if (!cursor) return;
        stop();
        // Left off the first chip returns to plain row navigation.
        if (cursor.index === 0) setChipCursor(null);
        else setChipCursor({ groupKey: group.key, index: cursor.index - 1 });
        return;

      case 'Escape':
        // First Escape leaves the chips, a second closes the list.
        if (!cursor) return;
        stop();
        setChipCursor(null);
        return;

      case 'ArrowDown':
      case 'ArrowUp':
        // Row navigation resumes; MUI moves the highlight.
        setChipCursor(null);
        return;

      default:
    }
  };

  return (
    <>
    <Autocomplete<DropdownOption>
      value={value}
      inputValue={inputValue}
      options={dropdownOptions}
      loading={loading}
      disabled={disabled}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => { setOpen(false); setChipCursor(null); }}
      onKeyDown={handleListKeyDown}
      onHighlightChange={(_e, option) => {
        setHighlighted(option as DropdownOption | null);
        // Highlight moved to another row — its chips are no longer in play.
        setChipCursor(prev => (
          prev && option && isGroupOption(option as DropdownOption)
            && (option as WineGroupOption).key === prev.groupKey ? prev : null
        ));
      }}
      filterOptions={x => x}
      getOptionLabel={option => {
        if (isCreateOption(option) || isGroupOption(option)) return '';
        return (option as WineSearchResult)?.name ?? '';
      }}
      isOptionEqualToValue={(option, val) => {
        if (isCreateOption(option) || isGroupOption(option)) return false;
        if (isCreateOption(val as DropdownOption) || isGroupOption(val as DropdownOption)) return false;
        return (option as WineSearchResult)._id === (val as WineSearchResult)._id;
      }}
      onInputChange={handleInputChange}
      onChange={(_e, newValue) => {
        if (!newValue) {
          onChange(null);
          setOptions([]);
          return;
        }
        // Group rows aren't selectable as a whole — their chips handle
        // selection. Ignore Enter/click on the row itself.
        if (isGroupOption(newValue)) return;
        if (isCreateOption(newValue)) {
          // Don't update selected value — open the appropriate modal flow.
          // VENUE_USER → internal RequestWineModal; ADMIN → parent-provided
          // onCreateNew callback (CreateWineModal).
          if (venueRequestMode) {
            setRequestModalQuery(inputValue || lastTypedQueryRef.current);
            setRequestModalOpen(true);
          } else if (adminCreateMode) {
            onCreateNew?.(inputValue || lastTypedQueryRef.current);
          }
          return;
        }
        onChange(newValue as WineSearchResult);
        setOptions([]);
      }}
      noOptionsText={noOptionsText}
      renderOption={(props, option) => {
        const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key: React.Key };

        if (isCreateOption(option)) {
          const hasResults = groupedOptions.length > 0;
          const createLabel = venueRequestMode
            ? (hasResults ? 'None of these? Request a new wine…' : 'Request new wine…')
            : (hasResults ? 'None of these? Create a new wine name…' : 'Create new wine…');
          return (
            <li key={key} {...rest}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, width: '100%', color: 'primary.main' }}>
                <CreateIcon fontSize="small" />
                <Typography variant="body2" fontWeight={600} color="primary.main">
                  {createLabel}
                </Typography>
              </Box>
            </li>
          );
        }

        const group = option as WineGroupOption;
        const canonicalColor = normalizeWineColor(group.color);
        const subtitle = [group.producerName, group.regionName].filter(Boolean).join(' · ');
        const cursorIndex = chipCursor?.groupKey === group.key ? chipCursor.index : null;
        // Virtual focus ring — real focus stays in the input (see chipCursor).
        const focusSx = {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: '1px',
        };
        return (
          <li key={key} {...rest} onClick={e => e.preventDefault()}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 0.5, width: '100%' }}>
              <Box
                sx={{
                  width: 9, height: 9, borderRadius: '50%', flexShrink: 0, mt: '5px',
                  backgroundColor: canonicalColor ? wineColorDotFor(group.color) : NEUTRAL_WINE_DOT,
                  opacity: canonicalColor ? 1 : 0.35,
                }}
              />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" fontWeight={500} noWrap>
                  {group.label || '—'}
                </Typography>
                {subtitle && (
                  <Typography variant="caption" color="text.secondary" display="block" noWrap>
                    {subtitle}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                  {group.wines.map((wine, i) => (
                    <Chip
                      key={wine._id}
                      label={wine.displayVintage || '—'}
                      size="small"
                      clickable
                      aria-current={cursorIndex === i ? 'true' : undefined}
                      sx={cursorIndex === i ? focusSx : undefined}
                      onMouseDown={e => e.preventDefault()}
                      onClick={e => { e.stopPropagation(); selectWine(wine); }}
                    />
                  ))}
                  {canAddYear && (
                    <Chip
                      label="+ add year"
                      size="small"
                      clickable
                      variant="outlined"
                      color="primary"
                      aria-current={cursorIndex === group.wines.length ? 'true' : undefined}
                      sx={cursorIndex === group.wines.length ? focusSx : undefined}
                      onMouseDown={e => e.preventDefault()}
                      onClick={e => { e.stopPropagation(); startAddYear(group); }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </li>
        );
      }}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          required
          error={error || searchFailed}
          helperText={
            helperText !== undefined
              ? helperText
              : !value && inputValue.trim().length < 2
                ? 'Type at least 2 characters to search'
                : undefined
          }
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress color="inherit" size={16} sx={{ mr: 0.5 }} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
    {addYearGroup && (
      <Box sx={{ mt: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 0 }} noWrap>
            Add year for <strong>{addYearGroup.label}</strong>:
          </Typography>
          <TextField
            size="small"
            label="Year"
            placeholder="2018"
            value={yearInput}
            autoFocus
            disabled={addYearBusy}
            onChange={e => { setYearInput(e.target.value); setAddYearError(''); }}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddYearConfirm(); } }}
            inputProps={{ maxLength: 4, 'aria-label': 'Year' }}
            sx={{ width: 90 }}
          />
          <Chip
            label="NV"
            size="small"
            clickable
            variant={yearInput.trim().toUpperCase() === 'NV' ? 'filled' : 'outlined'}
            color={yearInput.trim().toUpperCase() === 'NV' ? 'primary' : 'default'}
            disabled={addYearBusy}
            onClick={() => { setYearInput(prev => prev.trim().toUpperCase() === 'NV' ? '' : 'NV'); setAddYearError(''); }}
          />
          <Button
            size="small"
            variant="contained"
            disabled={addYearBusy || !yearInput.trim()}
            onClick={handleAddYearConfirm}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {addYearBusy ? <CircularProgress size={16} color="inherit" /> : 'Add'}
          </Button>
          <Button size="small" disabled={addYearBusy} onClick={resetAddYear} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
        </Box>
        {addYearError && (
          <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
            {addYearError}
          </Typography>
        )}
      </Box>
    )}
    {venueRequestMode && venueId && (
      <RequestWineModal
        open={requestModalOpen}
        venueId={venueId}
        initialQuery={requestModalQuery}
        onClose={() => setRequestModalOpen(false)}
        onCreated={(wine) => {
          // Select the freshly-requested wine on the form. The wine carries
          // needsReview=true; the parent form treats it like any other
          // selection — it's already linkable.
          onChange(toSearchResult(wine));
          setOptions([]);
          setRequestModalOpen(false);
        }}
      />
    )}
    </>
  );
};

export default WineSearchBox;
